import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangelogData } from '@compiere-ws/models/changelog-data';
import { ChangelogResponse } from '@compiere-ws/models/changelog-response';
import { ChangelogsService } from '@compiere-ws/services/changelogs/changelogs.service';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { EditViewUtils } from '@iupics-components/standard/layouts/edit-view-ui/utils/edit-view.utils';
import { AppConfig } from '@iupics-config/app.config.service';
import { IupicsSpecificWindow } from '@iupics-manager/models/iupics-data';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment/min/moment-with-locales';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, debounceTime, EMPTY, finalize, map, merge, Observable, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';

/** created / updated card content */
interface AuditStamp {
  by: string;
  absolute: string;
  relative: string;
}

type ChangelogState = 'loading' | 'ready' | 'new' | 'error';

@Component({
  selector: 'iu-changelog-window-ui',
  templateUrl: './changelog-window-ui.component.html',
  styleUrls: ['./changelog-window-ui.component.scss'],
  standalone: true,
  imports: [ButtonModule, TooltipModule, TranslateModule],
})
export default class ChangelogWindowUIComponent extends SpecificWindowUiComponent implements OnInit, OnDestroy {
  /** rows per request; one extra row is asked to know if there is more */
  readonly pageSize = 20;
  isCopiedToClipBoard = false;
  /** row/side of the value just copied (check icon) */
  copiedKey: string = null;
  #copiedTimer: ReturnType<typeof setTimeout>;
  state: ChangelogState = 'loading';
  auditsInfo = {
    tableName: '',
    id_txt: '',
  };
  created: AuditStamp | null = null;
  updated: AuditStamp | null = null;
  isLogged = false;
  rows: ChangelogData[] = [];
  hasMore = false;
  loadingMore = false;
  protected config = inject(AppConfig);
  protected changelogsService = inject(ChangelogsService);
  #cd = inject(ChangeDetectorRef);
  #destroyRef = inject(DestroyRef);
  #reload$ = new Subject<void>();
  #loadMoreSub: Subscription;
  #current: { tableId: number; recordId: string } | null = null;

  constructor() {
    super();
  }

  ngOnInit() {
    // set up before super.ngOnInit(): it calls buildWindow, which asks the first load
    const editView = this.parentComponent as EditViewUiComponent;
    const recordChange$: Observable<unknown> = editView?.currentDataStoreKeyChange ?? EMPTY;
    merge(this.#reload$, recordChange$)
      .pipe(
        // one request per burst of key changes (grid navigation, save), previous one cancelled
        debounceTime(60),
        switchMap(() => this.#load()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
    super.ngOnInit();
  }

  ngOnDestroy() {
    this.#loadMoreSub?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Je recupere les informations pour gerer la construction moi meme
   */
  buildWindow(specific: IupicsSpecificWindow, isCssOnComponent = true) {
    this.parentFormID = specific.AD_FormDetail_ID;
    this.title = specific.title;
    this.specificData = specific;
    this.state = 'loading';
    this.#reload$.next();
  }

  get isView(): boolean {
    return !!this.parentComponent?.editTabs?.[0]?.data?.isView;
  }

  dateFormater(value: Date | { value }) {
    if (value === null || value === undefined) {
      return '';
    }
    const valueToParse = value['value'] ? value['value'] : value;
    return DateUtils.formatLongLocaleStr(valueToParse);
  }

  relativeDate(value: Date | { value }): string {
    if (value === null || value === undefined) {
      return '';
    }
    const valueToParse = value['value'] ? value['value'] : value;
    return moment(valueToParse).fromNow();
  }

  #load(): Observable<void> {
    this.#loadMoreSub?.unsubscribe();
    this.loadingMore = false;
    const editView = this.parentComponent as EditViewUiComponent;
    const key = editView?.currentDataStoreKey;
    this.auditsInfo.tableName = editView?.data?.tableName ?? '';
    this.rows = [];
    this.hasMore = false;
    this.isLogged = false;
    if (!editView || !key?.recordId || this.store.isWindowNewData(key)) {
      // unsaved record: nothing to ask the server
      this.#current = null;
      this.created = this.updated = null;
      this.auditsInfo.id_txt = '';
      this.state = 'new';
      document.body.style.cursor = 'auto';
      this.#cd.markForCheck();
      return of(undefined);
    }
    const tableId = editView.data.AD_Table_ID;
    const recordId = key.recordId;
    this.#current = { tableId, recordId };
    this.state = 'loading';
    this.auditsInfo.id_txt = this.#formatIds(recordId);
    this.#cd.markForCheck();
    return this.changelogsService.getChangeLogInfo(tableId, recordId).pipe(
      switchMap((response) => {
        this.#applyInfo(response);
        return response?.isLogged ? this.changelogsService.getChangeLogs(tableId, recordId, 0, this.pageSize + 1) : of([]);
      }),
      tap((rows) => {
        this.#appendRows(rows ?? []);
        this.state = 'ready';
      }),
      map(() => undefined),
      catchError(() => {
        this.state = 'error';
        return of(undefined);
      }),
      finalize(() => {
        document.body.style.cursor = 'auto';
        this.#cd.markForCheck();
      })
    );
  }

  loadMore(): void {
    const current = this.#current;
    if (!current || this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadingMore = true;
    this.#loadMoreSub = this.changelogsService
      .getChangeLogs(current.tableId, current.recordId, this.rows.length, this.pageSize + 1)
      .pipe(
        finalize(() => {
          this.loadingMore = false;
          this.#cd.markForCheck();
        })
      )
      .subscribe({
        next: (rows) => {
          // drop a late reply for a previous record
          if (this.#current === current) {
            this.#appendRows(rows ?? []);
          }
        },
        error: () => (this.hasMore = false),
      });
  }

  #applyInfo(response: ChangelogResponse): void {
    this.isLogged = !!response?.isLogged;
    this.created = this.#stamp(response?.createdBy, response?.created);
    this.updated = this.#stamp(response?.updatedBy, response?.updated);
  }

  #stamp(by: string, date: Date): AuditStamp | null {
    if (!by && !date) {
      return null;
    }
    return { by: by ?? '', absolute: this.dateFormater(date ?? null), relative: this.relativeDate(date ?? null) };
  }

  #appendRows(rows: ChangelogData[]): void {
    this.hasMore = rows.length > this.pageSize;
    this.rows = [...this.rows, ...(this.hasMore ? rows.slice(0, this.pageSize) : rows)];
  }

  /** "AD_Org_ID,0,C_Order_ID,12" -> "AD_Org_ID:0 - C_Order_ID:12" */
  #formatIds(recordId: string): string {
    const parts = recordId.split(',');
    const out: string[] = [];
    for (let i = 0; i < parts.length; i += 2) {
      out.push(parts[i] + ':' + (parts[i + 1] ?? ''));
    }
    return out.join(' - ');
  }

  /** Copy a new / old value (e.g. to put an earlier value back), with a short confirmation */
  copyValue(value: string, key: string = null): void {
    if (value !== null && value !== undefined && value !== '') {
      EditViewUtils.copyToClipboard('' + value);
      this.copiedKey = key ?? 'value';
      this.isCopiedToClipBoard = true;
      clearTimeout(this.#copiedTimer);
      this.#copiedTimer = setTimeout(() => {
        this.copiedKey = null;
        this.isCopiedToClipBoard = false;
      }, 2000);
    }
  }

  /** "user - date time" of the created / updated card */
  copyStamp(stamp: AuditStamp, key: string): void {
    this.copyValue([stamp?.by, stamp?.absolute].filter((part) => !!part).join(' - '), key);
  }

  copyToClipBoard(str: string) {
    str = str.replace(/\:/g, '=').replace(/-/g, 'AND');
    EditViewUtils.copyToClipboard(str);
    this.copiedKey = null;
    this.isCopiedToClipBoard = true;
    setTimeout(() => {
      this.isCopiedToClipBoard = false;
    }, 3000);
  }
}
